import { useRef } from 'react';
import { useController, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, TextField } from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import { ConnectPopup } from './ConnectPopup';

const shopifySchema = z.object({
  domainName: z
    .string()
    .trim()
    .transform(value => value.replace('.myshopify.com', ''))
    .pipe(
      z
        .string()
        .min(1, 'Domain Name is required')
        .regex(
          /^(https?:\/\/)?[a-zA-Z0-9-]+(\.myshopify\.com)?$/,
          'Enter correct URL',
        ),
    ),
});

type ShopifyInput = z.input<typeof shopifySchema>;
type ShopifyValues = z.output<typeof shopifySchema>;

function extractHostname(url: string) {
  let hostname = url.indexOf('//') > -1 ? url.split('/')[2] : url.split('/')[0];
  hostname = hostname.split(':')[0];
  hostname = hostname.split('?')[0];
  return hostname;
}

/* Lives inside the dialog so that, with `shouldUnregister`, closing it clears the entry. */
function DomainField({ control }: { control: Control<ShopifyInput> }) {
  const { field, fieldState } = useController({ control, name: 'domainName' });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const message = fieldState.error?.message;

  return (
    <FormItem
      label="Shopify Store Domain"
      error={message}
      isRequired
      focusControl={() => inputRef.current?.focus()}
    >
      {({ labelId, errorId }) => (
        <div className="sk:flex sk:items-center sk:gap-2">
          <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col">
            <TextField
              aria-labelledby={labelId}
              aria-describedby={errorId}
              inputRef={element => {
                field.ref(element);
                inputRef.current = element;
              }}
              value={field.value}
              placeholder="yourstore"
              isInvalid={!!message}
              isRequired
              validationBehavior="aria"
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          </div>
          <span className="sk:shrink-0 sk:text-dense sk:text-fg-tertiary">
            .myshopify.com
          </span>
        </div>
      )}
    </FormItem>
  );
}

type ShopifyDomainPopupProps = {
  open: boolean;
  /** Gets the store's hostname, or '' when the user backs out. */
  closeModal: (store: string) => void;
  modalClassName?: string;
};

export function ShopifyDomainPopup({
  open,
  closeModal,
  modalClassName = '',
}: ShopifyDomainPopupProps) {
  const { handleSubmit, control } = useForm<
    ShopifyInput,
    unknown,
    ShopifyValues
  >({
    resolver: zodResolver(shopifySchema),
    defaultValues: { domainName: '' },
    shouldUnregister: true,
  });
  const onSubmit = (data: ShopifyValues) => {
    closeModal(extractHostname(`${data.domainName}.myshopify.com`));
  };

  return (
    <ConnectPopup
      open={open}
      title="Connect your Shopify Store"
      onClose={() => closeModal('')}
      onSubmit={handleSubmit(onSubmit)}
      className={modalClassName}
    >
      <p className="sk:m-0 sk:text-fg-secondary">
        Please enter your Shopify store domain name below.
      </p>
      <DomainField control={control} />
    </ConnectPopup>
  );
}

type ShopifyNoticePopupProps = {
  open: boolean;
  /** True when the user has read the notice and wants to go on. */
  closeModal: (proceed: boolean) => void;
  modalClassName?: string;
};

export function ShopifyNoticePopup({
  open,
  closeModal,
  modalClassName = '',
}: ShopifyNoticePopupProps) {
  return (
    <ConnectPopup
      open={open}
      title="Action Required: Uninstall Conflicting Apps"
      onClose={() => closeModal(false)}
      className={modalClassName}
      footer={
        <>
          <Button onPress={() => closeModal(false)}>Cancel</Button>
          <Button variant="primary" onPress={() => closeModal(true)}>
            Ok, Got it
          </Button>
        </>
      }
    >
      <p className="sk:m-0">
        To prevent double fulfillments, please uninstall any conflicting apps,
        such as:
      </p>
      <ul className="sk:m-0 sk:pl-5">
        <li>Amazon MCF</li>
        <li>Web Bee</li>
        <li>Marketplace Connect</li>
      </ul>
      <p className="sk:m-0">
        This ensures smooth order processing and avoids fulfillment issues.
      </p>
    </ConnectPopup>
  );
}

type MiraklContactSalesPopupProps = {
  open: boolean;
  closeModal: () => void;
  modalClassName?: string;
};

export function MiraklContactSalesPopup({
  open,
  closeModal,
  modalClassName = '',
}: MiraklContactSalesPopupProps) {
  return (
    <ConnectPopup
      open={open}
      title="Mirakl Integration"
      onClose={() => closeModal()}
      size="sm"
      className={modalClassName}
      footer={
        <Button variant="primary" onPress={() => closeModal()}>
          OK
        </Button>
      }
    >
      <p className="sk:m-0 sk:text-fg-secondary">
        Please contact sales team for Mirakl integration.
      </p>
    </ConnectPopup>
  );
}
